import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useReports, useDeleteReport, useExportReport } from '@/hooks/reporting/useReports';
import ReportsTable from '@/components/reports/tables/ReportsTable';
import ReportTypeSelector from '@/components/reports/filters/ReportTypeSelector';
import DateRangePicker from '@/components/reports/filters/DateRangePicker';
import ConfirmDeleteModal from '@/components/reports/modals/ConfirmDeleteModal';
import EmptyState from '@/components/reports/common/EmptyState';
import type { ListReportsQuery, ReportType } from '@/types/reporting-v2';

export default function SavedReports() {
  const navigate = useNavigate();
  const deleteReport = useDeleteReport();
  const exportReport = useExportReport();

  const [query, setQuery] = useState<ListReportsQuery>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useReports(query);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<ReportType | undefined>();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    snapshotId?: string;
    reportName?: string;
  }>({
    isOpen: false,
  });

  const handleSearch = () => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      search: searchTerm || undefined,
    }));
  };

  const handleTypeFilter = (type: ReportType | undefined) => {
    setSelectedType(type);
    setQuery((prev) => ({
      ...prev,
      page: 1,
      report_type: type,
    }));
  };

  const handleDateRangeFilter = (start: string, end: string) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      start_date: start,
      end_date: end,
    }));
  };

  const handlePageChange = (page: number) => {
    setQuery((prev) => ({ ...prev, page }));
  };

  const handleView = (snapshotId: string) => {
    navigate(`/admin/reporting/reports/${snapshotId}`);
  };

  const handleExport = async (snapshotId: string, format: 'pdf' | 'excel') => {
    await exportReport.mutateAsync({ snapshotId, format });
  };

  const handleDeleteClick = (snapshotId: string, reportName: string) => {
    setDeleteModal({
      isOpen: true,
      snapshotId,
      reportName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.snapshotId) {
      await deleteReport.mutateAsync(deleteModal.snapshotId);
      setDeleteModal({ isOpen: false });
      refetch();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saved Reports</h1>
          <p className="text-sm text-gray-500 mt-1">
            View, export, and manage your generated reports
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/reporting/reports/generate')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Generate Report
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="space-y-4">
          {/* Search */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search reports by name or description..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Search
            </button>
          </div>

          {/* Type and Date Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type
              </label>
              <ReportTypeSelector
                value={selectedType}
                onChange={handleTypeFilter}
                variant="dropdown"
                allowClear
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Generated Date Range
              </label>
              <DateRangePicker
                startDate={query.start_date || ''}
                endDate={query.end_date || ''}
                onChange={handleDateRangeFilter}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {data?.reports && data.reports.length > 0 ? (
          <>
            <ReportsTable
              reports={data.reports}
              loading={isLoading}
              onView={handleView}
              onExport={handleExport}
              onDelete={(id) => {
                const report = data.reports.find((r) => {
                  const reportId = (r as any).report_id || r.snapshot_id;
                  return reportId === id;
                });
                if (report) {
                  const reportName = (report as any).report_name || report.report_type;
                  handleDeleteClick(id, reportName);
                }
              }}
            />

            {/* Pagination */}
            {data.pagination && data.pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(data.pagination.page - 1) * data.pagination.limit + 1} to{' '}
                  {Math.min(
                    data.pagination.page * data.pagination.limit,
                    data.pagination.total
                  )}{' '}
                  of {data.pagination.total} reports
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(query.page! - 1)}
                    disabled={query.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: data.pagination.total_pages }, (_, i) => i + 1)
                      .filter(
                        (page) =>
                          page === 1 ||
                          page === data.pagination!.total_pages ||
                          Math.abs(page - query.page!) <= 2
                      )
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-3 py-1">...</span>
                          )}
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 border rounded-md ${
                              query.page === page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(query.page! + 1)}
                    disabled={query.page === data.pagination.total_pages}
                    className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : isLoading ? (
          <div className="p-6">
            <ReportsTable reports={[]} loading={true} onView={() => {}} onExport={() => {}} onDelete={() => {}} />
          </div>
        ) : (
          <div className="p-12">
            <EmptyState
              icon={Plus}
              title="No reports found"
              description="Get started by generating your first report"
              action={{
                label: 'Generate Report',
                onClick: () => navigate('/admin/reporting/reports/generate'),
              }}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={handleDeleteConfirm}
        title="Delete Report"
        message="Are you sure you want to delete this report? This action cannot be undone."
        itemName={deleteModal.reportName}
        loading={deleteReport.isPending}
      />
    </div>
  );
}
